import AppLogo from '@/components/layout/app-logo';
import ThemeToggle from '@/components/shared/theme-toggle';
import { Button } from '@/components/ui/button';
import Icon from '@/icons/icon';
import { Link, router, usePage } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';

const navPill =
    'inline-flex h-10 cursor-pointer items-center justify-center rounded-full border border-white/40 px-4 text-sm font-medium text-white transition hover:bg-white/10';

export default function Header() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [avatarOpen, setAvatarOpen] = useState(false);
    const avatarRef = useRef<HTMLDivElement>(null);

    const { auth } = usePage().props as {
        auth?: {
            user?: {
                roles?: { name: string }[];
            } | null;
            customer?: {
                first_name: string;
                last_name: string;
                profile_image?: string | null;
            } | null;
        };
    };
    const isLoggedIn = !!auth?.customer;
    const adminRoles = ['super_admin', 'admin', 'motel_admin', 'manager'];
    const isAdmin =
        !!auth?.user &&
        auth?.user?.roles?.some((r) => adminRoles.includes(r.name));

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                avatarRef.current &&
                !avatarRef.current.contains(event.target as Node)
            ) {
                setAvatarOpen(false);
            }
        }

        if (avatarOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [avatarOpen]);

    const handleLogout = () => {
        router.post('/logout');
    };

    return (
        <header className="sticky top-0 z-50 w-full bg-[#003580] shadow-md">
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                {/* LOGO */}
                <Link href="/" className="flex items-center">
                    <AppLogo light />
                </Link>

                {/* DESKTOP NAV */}
                <nav className="hidden items-center gap-5 text-sm font-medium whitespace-nowrap text-white lg:flex">
                    <Link
                        href="/about-us"
                        className="flex items-center gap-1.5 transition hover:text-gray-200"
                    >
                        About us
                    </Link>
                    <Link
                        href="/customer-support"
                        className="flex items-center gap-1.5 transition hover:text-gray-200"
                    >
                        Contact us
                    </Link>
                    <a
                        href="/customer-support"
                        className="flex items-center gap-1.5 transition hover:text-gray-200"
                    >
                        List your property
                    </a>

                    {isAdmin ? (
                        <div className="flex items-center gap-3">
                            <Button
                                variant="bare"
                                onClick={() => router.visit('/admin')}
                                className={navPill}
                            >
                                Admin portal
                            </Button>

                            <Button
                                variant="bare"
                                onClick={handleLogout}
                                className={navPill}
                            >
                                Sign out
                            </Button>
                        </div>
                    ) : !isLoggedIn ? (
                        <div className="flex items-center gap-3">
                            {/* Login */}
                            <Link href="/login" className={navPill}>
                                Sign in
                            </Link>
                            {/* Register */}
                            <Link href="/register" className={navPill}>
                                Create account
                            </Link>
                        </div>
                    ) : (
                        <div className="relative" ref={avatarRef}>
                            <Button
                                variant="bare"
                                onClick={() => setAvatarOpen(!avatarOpen)}
                                className="flex cursor-pointer items-center gap-1 rounded-full border border-white/40 px-3 py-1 transition hover:bg-white/10"
                            >
                                <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full text-xs font-bold text-white">
                                    {auth?.customer?.profile_image ? (
                                        <img
                                            src={auth.customer.profile_image}
                                            className="h-full w-full rounded-full border-2 border-yellow-500 object-cover"
                                            alt="avatar"
                                        />
                                    ) : (
                                        <Icon
                                            iconName="user"
                                            className="h-6 w-6 rounded-full border-2 border-yellow-500 stroke-white"
                                        />
                                    )}
                                </div>
                                {auth?.customer?.first_name}{' '}
                                {auth?.customer?.last_name}
                            </Button>

                            {avatarOpen && (
                                <div className="animate-slideDown absolute right-0 mt-4 w-56 rounded-md border border-border bg-card text-sm text-card-foreground shadow-xl">
                                    <Link
                                        href="/my-account"
                                        className="flex items-center gap-3 rounded-md border-b border-border px-4 py-3 transition hover:bg-muted"
                                    >
                                        <Icon
                                            iconName="user"
                                            className="h-5 w-5 stroke-muted-foreground"
                                        />
                                        <span>My account</span>
                                    </Link>
                                    <Link
                                        href="/bookings"
                                        className="flex items-center gap-3 border-b border-border px-4 py-3 transition hover:bg-muted"
                                    >
                                        <Icon
                                            iconName="Handbag"
                                            className="h-5 w-5 stroke-muted-foreground"
                                        />
                                        <span>Bookings </span>
                                    </Link>

                                    <Link
                                        href="/saved"
                                        className="flex items-center gap-3 border-b border-border px-4 py-3 transition hover:bg-muted"
                                    >
                                        <Icon
                                            iconName="Heart"
                                            className="h-5 w-5 stroke-muted-foreground"
                                        />
                                        <span>Saved</span>
                                    </Link>
                                    <Button
                                        variant="bare"
                                        onClick={handleLogout}
                                        className="flex w-full cursor-pointer items-center gap-3 rounded-md px-4 py-3 text-left transition hover:bg-muted"
                                    >
                                        <Icon
                                            iconName="Signout"
                                            className="h-5 w-5 fill-muted-foreground"
                                        />
                                        <span>Sign out</span>
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    <ThemeToggle className="size-auto text-white hover:bg-transparent hover:text-gray-200" />
                </nav>

                {/* MOBILE: THEME TOGGLE + MENU BUTTON */}
                <div className="flex items-center gap-2 lg:hidden">
                    <ThemeToggle className="text-white hover:bg-white/10" />
                    <Button
                        variant="bare"
                        onClick={() => setMobileOpen(!mobileOpen)}
                        className="p-2 text-white"
                    >
                        {mobileOpen ? (
                            <Icon
                                iconName="X"
                                className="h-7 w-7 stroke-white"
                            />
                        ) : (
                            <Icon
                                iconName="Menu"
                                className="h-7 w-7 stroke-white"
                            />
                        )}
                    </Button>
                </div>
            </div>

            {/* MOBILE NAV */}
            {mobileOpen && (
                <div className="animate-slideDown border-t border-white/20 bg-[#003580] text-white lg:hidden">
                    <div className="space-y-5 px-4 py-5 text-base">
                        {isLoggedIn && (
                            <div className="flex items-center gap-3 pt-2">
                                <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-gray-300 text-sm font-bold text-white">
                                    {auth?.customer?.profile_image ? (
                                        <img
                                            src={auth.customer.profile_image}
                                            className="h-full w-full object-cover"
                                            alt="avatar"
                                        />
                                    ) : (
                                        <Icon
                                            iconName="user"
                                            className="h-6 w-6 stroke-white"
                                        />
                                    )}
                                </div>
                                <div className="font-medium">
                                    {' '}
                                    {auth?.customer?.first_name}{' '}
                                    {auth?.customer?.last_name}
                                </div>
                            </div>
                        )}

                        <Link
                            href="/about-us"
                            className="flex items-center gap-2 font-medium"
                        >
                            About us
                        </Link>
                        <Link
                            href="/customer-support"
                            className="flex items-center gap-2 font-medium"
                        >
                            Contact us
                        </Link>
                        <a
                            href="/customer-support"
                            className="flex items-center gap-2 font-medium"
                        >
                            List your property
                        </a>

                        <div className="h-[1px] w-full bg-white/30"></div>

                        {isAdmin ? (
                            <>
                                <Button
                                    variant="bare"
                                    onClick={() => router.visit('/admin')}
                                    className="text-white-300 block font-medium"
                                >
                                    Admin portal
                                </Button>
                                <Button
                                    variant="bare"
                                    onClick={handleLogout}
                                    className="block w-full text-left font-medium text-red-300"
                                >
                                    Sign out
                                </Button>
                            </>
                        ) : !isLoggedIn ? (
                            <>
                                <Link
                                    href="/register"
                                    className="block font-medium"
                                >
                                    Create account
                                </Link>
                                <Link
                                    href="/login"
                                    className="block font-medium"
                                >
                                    Sign in
                                </Link>
                            </>
                        ) : (
                            <>
                                <Link
                                    href="/my-account"
                                    className="block font-medium"
                                >
                                    My account
                                </Link>
                                <Link
                                    href="/bookings"
                                    className="block font-medium"
                                >
                                    Bookings
                                </Link>

                                <Link
                                    href="/saved"
                                    className="block font-medium"
                                >
                                    Saved
                                </Link>
                                <Button
                                    variant="bare"
                                    onClick={handleLogout}
                                    className="block w-full text-left font-medium text-red-300"
                                >
                                    Sign out
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
